import * as THREE from 'three';
import { ERM_LAMP, ERM_MIRROR, ERM_SLOT, ERM_SPLIT, ERM_STONE, ermLampReady, hexToWorld, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A clay lamp, its flame. */
const LAMP = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 10.5h11c-.6 1.9-2.7 3-5.5 3s-4.9-1.1-5.5-3z" fill="currentColor"/><path d="M8 2c1.6 1.8 2.3 3.1 1.5 4.7.1-.9-.5-1.4-1-1.8-.1 1.2-1.5 1.6-1.5 2.8-.8-1.5-.3-3.2 1-5.7z" fill="currentColor"/><path d="M8 8.5v2" stroke="currentColor" stroke-width="1.2"/></svg>';
/** The Doliente: a tall veiled figure, the veil trailing. */
const VEIL = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="3.2" r="1.8" fill="currentColor"/><path d="M5 5.5c0 3-1 6.5-2.5 9h6c-.4-2.8-.4-6.2 0-9zM9 6.2c2 2.4 3.6 5.6 5 8.3h-3.4c-.6-2.6-1-5.2-1.6-8.3z" fill="currentColor"/></svg>';
/** Her wail: a mouth and its waves. */
const WAIL = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="5" cy="8" rx="2" ry="3" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M9 5.2c1 .8 1 4.8 0 5.6M11.2 3.6c1.8 1.4 1.8 7.4 0 8.8M13.4 2.2c2.4 2 2.4 9.6 0 11.6" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** A mirror on its column. */
const MIRROR = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="6.5" rx="4.6" ry="4.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M5.6 5.2l3-2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M8 11.1V14.5M5.5 14.5h5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** The altar, a lit line over it. */
const ALTAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5h11v2h-11zM4 10.5h8v4H4z" fill="currentColor"/><path d="M8 1.5v4.5M5.5 3.5 8 6l2.5-2.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** A bone singer: a skull and a note. */
const SINGER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.5 2a4.5 4.5 0 0 0-4.5 4.5c0 1.6.8 2.6 1.8 3.2V12h5.4V9.7c1-.6 1.8-1.6 1.8-3.2A4.5 4.5 0 0 0 6.5 2z" fill="currentColor"/><circle cx="4.9" cy="6.7" r="1" fill="#0b0c10"/><circle cx="8.1" cy="6.7" r="1" fill="#0b0c10"/><path d="M12.5 3v7.2" stroke="currentColor" stroke-width="1.2"/><ellipse cx="11.6" cy="10.6" rx="1.3" ry="1" fill="currentColor"/></svg>';
/** A loose stone in the wall. */
const STONE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 3h12v10H2z" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M5 6h6v4H5z" fill="currentColor"/><path d="M11.5 8h2.5" stroke="currentColor" stroke-width="1.2"/></svg>';

type Mark = { el: HTMLButtonElement; at: number; point: THREE.Vector3; state: HTMLElement; name: HTMLElement };

const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));

/**
 * The cleric's last road on screen, chapter 4 (docs/sendas/clerigo.md, «La Ermita»), inside a path's own plate and over
 * the board:
 *
 * - the memory of Aud's vigil: the lamps lit of six and when the next one can be lit; where the Doliente has come to;
 *   her wail (when it comes, warned: no heals, quiet her with light; in it; quiet). Over the board, a mark on each lamp
 *   not lit (its number, the altar's its own; «in n s» until its minute comes; a tap sends the hero to light it).
 * - the nave of the mirrors: Aud's mirror (in the stone, in the hands, in its place); the altar and the door (dark, lit;
 *   the beam split twice); the singer's dirge. Over the board, the loose stone, Aud's mirror's place, each mirror (its
 *   number; «covered» under a limpet) and each divider.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class ErmitaHud {
  readonly panel: HTMLElement;
  private kind: 'vigil' | 'nave';
  private v = new THREE.Vector3();
  private rowA = h('div.bh-row.al-waverow.er-a');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow.er-b');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow.er-c');
  private textC = h('span.al-wave');
  private rowD = h('div.bh-row.al-waverow.er-d');
  private textD = h('span.al-wave');
  private marks: Mark[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const H = s.rules?.senda?.litter?.hermit;
    this.kind = H?.vigil ? 'vigil' : 'nave';
    const rows: HTMLElement[] = [];
    if (H?.vigil) {
      this.rowA.append(h('span.bh-ico', { html: LAMP }), h('span.bh-label', {}, t('senda.camino.lamps')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: VEIL }), h('span.bh-label', {}, t('senda.camino.doliente')), this.textB);
      this.rowC.append(h('span.bh-ico', { html: WAIL }), h('span.bh-label', {}, t('senda.camino.wail')), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
      const last = H.vigil.lamps.length - 1;
      H.vigil.lamps.forEach((l, i) => this.addMark(ERM_LAMP + i, l, i === last ? ALTAR : LAMP, i === last ? t('senda.camino.lampAltar') : t('senda.camino.lampMark', { n: i + 1 }), t('senda.camino.lampHint'), 1.4, i === last ? '' : String(i + 1)));
    } else if (H?.nave) {
      this.rowA.append(h('span.bh-ico', { html: MIRROR }), h('span.bh-label', {}, t('senda.camino.audMirror')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: ALTAR }), h('span.bh-label', {}, t('senda.camino.light')), this.textB);
      this.rowD.append(h('span.bh-ico', { html: SINGER }), h('span.bh-label', {}, t(H.nave.singer === 0 ? 'senda.camino.hail' : 'senda.camino.singer')), this.textD);
      rows.push(this.rowA, this.rowB, this.rowD);
      this.addMark(ERM_STONE, H.nave.stone, STONE, t('senda.camino.stoneMark'), t('senda.camino.stoneHint'), 0.9);
      const slot = H.mirrors[H.nave.aud] as { q: number; r: number };
      this.addMark(ERM_SLOT, hexToWorld(slot.q, slot.r), MIRROR, t('senda.camino.slotMark'), t('senda.camino.slotHint'), 2.4);
      H.mirrors.forEach((m, i) => this.addMark(ERM_MIRROR + i, hexToWorld(m.q, m.r), MIRROR, `${t('senda.camino.mirror')} ${i + 1}`, t('senda.camino.mirrorHint'), 2.6, String(i + 1)));
      H.splits.forEach((m, i) => this.addMark(ERM_SPLIT + i, hexToWorld(m.q, m.r), MIRROR, `${t('senda.camino.split')} ${'AB'[i]}`, t('senda.camino.splitHint'), 2.6, 'AB'[i] as string));
    }
    this.panel = h(`div.bh.nv-panel.er-panel.er-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, p: { x: number; y: number }, ico: string, name: string, hint: string, lift: number, num = ''): void {
    const icon = num ? h('span.cm-ico.nv-num', {}, num) : h('span.cm-ico', { html: ico });
    const state = h('b.nv-state');
    const nm = h('b.mn-name.cm-name', {}, name);
    const cls = at >= ERM_SPLIT && at < ERM_SPLIT + 2 ? '.er-split' : at >= ERM_MIRROR && at < ERM_MIRROR + 6 ? '.nv-mirror' : at >= ERM_LAMP && at < ERM_LAMP + 6 ? (num ? '.er-lamp' : '.er-lamp.er-altar') : '.er-stone';
    const el = h(
      `button.al-stone.cm-mark.nv-mark${cls}`,
      { type: 'button', 'data-test': `ermita-mark-${at}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      icon,
      nm,
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, at, point: simToWorld(p.x, p.y, new THREE.Vector3(), lift), state, name: nm });
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.litter;
    const w = s.senda?.litter;
    const H = r?.hermit;
    const m = w?.erm;
    if (!r || !w || !H || !m) return;
    const set = (el: HTMLElement, text: string): void => {
      if (el.textContent !== text) el.textContent = text;
    };
    const V = H.vigil;
    const N = H.nave;
    if (V) {
      const lit = m.lit.reduce((a, v) => a + v, 0);
      const next = m.lit.findIndex((v, i) => !v && ermLampReady(H, i, s.waveT));
      const soon = m.lit.findIndex((_, i) => !ermLampReady(H, i, s.waveT));
      const lampT = next >= 0 ? (next === V.lamps.length - 1 ? t('senda.camino.lampReadyAltar') : t('senda.camino.lampReady', { n: next + 1 })) : soon >= 0 ? t('senda.camino.lampNext', { n: secs(V.every * soon - s.waveT) }) : '';
      set(this.textA, [t('senda.camino.lampsOf', { n: lit }), lampT].filter(Boolean).join(' · '));
      this.rowA.classList.toggle('warn', next >= 0);
      const boss = m.boss ? s.enemies.find((e) => e.id === m.boss && e.hp > 0) : undefined;
      set(this.textB, !boss ? t('senda.camino.dolOut') : m.front < 0 ? t('senda.camino.dolDoor') : t('senda.camino.dolAt', { n: m.front + 1 }));
      this.rowB.classList.toggle('warn', m.front >= 3);
      const on = s.waveT < m.wailUntil;
      const warn = !on && boss && m.wail - s.waveT <= V.wail.warn;
      const quiet = s.waveT < m.mute;
      set(this.textC, !boss ? '—' : on ? t('senda.camino.wailOn') : quiet ? t('senda.camino.wailMute', { n: secs(m.mute - s.waveT) }) : warn ? t('senda.camino.wailWarn', { n: secs(m.wail - s.waveT) }) : t('senda.camino.wailNext', { n: secs(m.wail - s.waveT) }));
      this.rowC.classList.toggle('warn', !!on || !!warn);
      this.rowC.classList.toggle('good', quiet);
    } else if (N) {
      set(this.textA, t(m.aud === 0 ? 'senda.camino.audStone' : 'senda.camino.audHand'));
      this.rowA.classList.toggle('warn', m.aud < 2);
      // (Set in its place, the row goes: a phone's plate stays short.)
      setStyle(this.rowA, 'display', m.aud === 2 ? 'none' : '');
      const weak = m.weak.some((k) => k >= 2);
      const both = m.altar && m.door;
      const on = (x: boolean): string => t(x ? 'senda.camino.lit' : 'senda.camino.dark');
      set(this.textB, both ? t('senda.camino.both', { n: secs(N.hold - (s.tick - m.both)) }) : t('senda.camino.lightOf', { a: on(m.altar), d: on(m.door) }) + (weak ? ` · ${t('senda.camino.weakBeam')}` : ''));
      this.rowB.classList.toggle('good', both);
      this.rowB.classList.toggle('warn', weak && !both);
      const sg = s.enemies.find((e) => e.id === m.singer && e.hp > 0);
      const left = m.dirge - s.waveT;
      // (Chapter 5's mirrors again: no singer, the wind's hail on the same clock.)
      const hail = N.singer === 0;
      set(
        this.textD,
        hail ? (left <= N.dirge.warn ? t('senda.camino.hailWarn', { n: secs(left) }) : t('senda.camino.hailNext', { n: secs(left) })) : !sg ? t('senda.camino.singerDown') : left <= N.dirge.warn ? t('senda.camino.singerWarn', { n: secs(left) }) : t('senda.camino.singerNext', { n: secs(left) }),
      );
      this.rowD.classList.toggle('warn', (hail || !!sg) && left <= N.dirge.warn);
      this.rowD.classList.toggle('good', !sg && !hail);
    }
    // The marks.
    const readyKey = V ? m.lit.map((_, i) => (ermLampReady(H, i, s.waveT) ? 1 : 0)).join('') : '';
    const k = `${m.lit.join('')}|${readyKey}|${m.front}|${m.aud}|${m.covered}|${m.both}|${s.result}|${V ? Math.floor(s.waveT / 12) : ''}`;
    if (k !== this.key) {
      this.key = k;
      for (const mk of this.marks) {
        let gone = s.result !== 'running';
        let text = '';
        if (V && mk.at >= ERM_LAMP && mk.at < ERM_LAMP + 6) {
          const i = mk.at - ERM_LAMP;
          gone ||= !!m.lit[i];
          const ready = ermLampReady(H, i, s.waveT);
          mk.el.classList.toggle('wait', !ready);
          // (Only the next one to come counts down: six counters at once read as noise.)
          const first = m.lit.findIndex((_, j) => !ermLampReady(H, j, s.waveT));
          if (!ready && i !== first) gone = true;
          text = ready ? '' : t('senda.camino.lampWait', { n: secs(V.every * i - s.waveT) });
        } else if (N) {
          if (mk.at === ERM_STONE) gone ||= m.aud !== 0;
          else if (mk.at === ERM_SLOT) gone ||= m.aud !== 1;
          else if (mk.at >= ERM_MIRROR && mk.at < ERM_MIRROR + 6) {
            const i = mk.at - ERM_MIRROR;
            gone ||= i === N.aud && m.aud !== 2;
            const cov = ((m.covered >> i) & 1) === 1;
            mk.el.classList.toggle('covered', cov);
            text = cov ? t('senda.camino.covered') : '';
          }
          if (m.both >= 0) gone = true;
        }
        if (mk.state.textContent !== text) mk.state.textContent = text;
        mk.el.classList.toggle('gone', gone);
        mk.el.disabled = s.result !== 'running';
        remeasureMark(mk.el);
      }
    }
    for (const mk of this.marks) {
      const sent = w.job === mk.at;
      mk.el.classList.toggle('sent', sent);
      const need = V && mk.at < ERM_MIRROR ? V.light : N && (mk.at === ERM_STONE || mk.at === ERM_SLOT) ? N.take : H.stand;
      mk.el.style.setProperty('--k', sent ? Math.min(1, Math.max(0, w.held) / Math.max(1, need)).toFixed(2) : '0');
    }
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
